import mitt from 'mitt';
import Keycloak from 'keycloak-js';
import { log } from '@/app/shared/utils';
import type { Transport } from './transport';
import type { AuthState, UserInfo, UserInfoUpdatePayload } from './types';
interface AuthOptions {
  url: string;
  realm: string;
  clientId: string;
  silentCheckSsoRedirectUri: string;
  logoutRedirectUri: string;
  transport: Transport;
}
export class Auth {
  private transport: Transport;
  private emitter = mitt<{
    change: AuthState;
  }>();
  private readonly logoutRedirectUri: string;
  private readonly silentCheckSsoRedirectUri: string;
  private readonly keycloak: Keycloak;
  private readonly keycloakEnabled: boolean;
  private state: AuthState = 'unauthenticated';
  constructor(options: AuthOptions) {
    this.transport = options.transport;
    this.logoutRedirectUri = options.logoutRedirectUri;
    this.silentCheckSsoRedirectUri = options.silentCheckSsoRedirectUri;
    this.keycloakEnabled = Boolean(options.url && options.realm && options.clientId);
    this.keycloak = new Keycloak({
      url: options.url,
      realm: options.realm,
      clientId: options.clientId,
    });
    this.keycloak.onTokenExpired = () => this.keycloak.updateToken(0);
    this.keycloak.onAuthSuccess = () => {
      log('keycloak auth success');
      this.setState('authenticated');
    };
    this.keycloak.onAuthLogout = () => {
      this.setState('unauthenticated');
    };
    this.keycloak.onAuthRefreshError = () => {
      log('keycloak auth refresh error');
      this.setState('error');
      this.keycloak.clearToken();
    };
    this.keycloak.onAuthError = () => {
      log('keycloak auth error');
      this.setState('error');
      this.keycloak.clearToken();
    };
  }
  private setState(state: AuthState) {
    this.state = state;
    this.emitter.emit('change', state);
  }
  getState(): AuthState {
    return this.state;
  }
  async init() {
    if (!this.keycloakEnabled) {
      this.setState('authenticated');
      return;
    }
    try {
      await this.keycloak.init({
        checkLoginIframe: false,
        enableLogging: false,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: this.silentCheckSsoRedirectUri,
      });
    } catch (error) {
      log('Failed to initialize keycloak');
      this.setState('error');
    }
  }
  async logout(redirectUri?: string) {
    if (!this.keycloakEnabled) {
      this.setState('unauthenticated');
      return;
    }
    await this.keycloak.logout({
      redirectUri:
        redirectUri ??
        this.keycloak.createLoginUrl({
          redirectUri: this.logoutRedirectUri,
        }),
    });
  }
  async login(redirectUri?: string) {
    if (!this.keycloakEnabled) {
      return;
    }
    await this.keycloak.login({
      prompt: 'login',
      redirectUri,
    });
  }
  async getToken() {
    if (!this.keycloakEnabled) {
      return undefined;
    }
    try {
      await this.keycloak.updateToken(30);
      return this.keycloak.token;
    } catch (error) {
      log('Failed to get keycloak token');
      this.logout();
    }
  }
  isAuthenticated(): boolean {
    return this.state === 'authenticated';
  }
  async getUserInfo(): Promise<UserInfo | undefined> {
    if (!this.keycloakEnabled) {
      return undefined;
    }
    const info = await this.keycloak.loadUserInfo();
    return info as UserInfo;
  }
  async updateUserInfo(data: UserInfoUpdatePayload): Promise<void> {
    if (!this.keycloakEnabled) {
      return;
    }
    const accountUrl = this.keycloak.createAccountUrl().split('?')[0];
    await this.transport.post(accountUrl, data);
  }
  async changeUserPassword() {
    if (!this.keycloakEnabled) {
      return undefined;
    }
    return this.keycloak.login({ action: 'UPDATE_PASSWORD' });
  }
  createLoginUrl(redirectUri: string) {
    if (!this.keycloakEnabled) {
      return '';
    }
    return this.keycloak.createLoginUrl({
      redirectUri,
    });
  }
  onStateChanged(handler: (state: AuthState) => void) {
    this.emitter.on('change', handler);
    return () => this.emitter.off('change', handler);
  }
}
